import React, { useState, useEffect } from 'react';
import SettingsPanel from '../components/SettingsPanel';
import AppCard from '../components/AppCard';
import OrgSwitcher from '../components/OrgSwitcher';
import TrialBar from '../components/TrialBar';
import UpdatesFeed from '../components/UpdatesFeed';
import Roadmap from '../components/Roadmap';
import SmartData from './SmartData';
import SmartFinance from './SmartFinance';
import SmartShopHome from './SmartShopHome';
import SmartComContacts from './SmartComContacts';
import PlanBar from '../components/PlanBar';
import ReferralCard from '../components/ReferralCard';
import UsageBars from '../components/UsageBars';
import { SOLUTIONS, deriveTileState, isClickable } from '../solutions';

function Dashboard({ user, gido, quota, sub, storageUsedGb, fileCount, onLogout, onRefresh, onRefreshQuota, openUpgrade, orgs, activeGido, onSelectOrg }) {

  const [currentApp, setCurrentApp] = useState(
    new URLSearchParams(window.location.search).get('app') || null
  );
  const [showWelcome, setShowWelcome]   = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [glow, setGlow] = useState(null);
  const [bookmarkDismissed, setBookmarkDismissed] = useState(
    localStorage.getItem('dbos_bookmark_dismissed') === 'true'
  );

  const params = new URLSearchParams(window.location.search);
  const isFirstLogin = params.get('welcome') === 'true';

  useEffect(() => {
    if (isFirstLogin) {
      setShowWelcome(true);
      // Clean URL without reload
      window.history.replaceState({}, '', '/');
    }
  }, [isFirstLogin]);

  // Keep current app in the URL so refresh / reload stays in place
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (currentApp && p.get('app') !== currentApp) {
      window.history.replaceState({}, '', `?app=${currentApp}`);
    } else if (!currentApp && p.get('app')) {
      window.history.replaceState({}, '', '/');
    }
  }, [currentApp]);

  const companyName = gido?.company_name || gido?.display_name || 'SmartClient';

  useEffect(() => {
    async function loadLogo() {
      if (!gido?.gido_id) return;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const snap = await getDoc(doc(db, 'smart_brand', gido.gido_id));
        if (snap.exists()) {
          const v = snap.data().visual || {};
          if (v.logo_url) setLogoUrl(v.logo_url);
          if (v.glow_mode && v.glow_mode !== 'off') {
            setGlow({ mode: v.glow_mode, single_color: v.glow_color, colors: v.colors || {} });
          }
        }
      } catch (e) { console.error('logo load failed', e); }
    }
    loadLogo();
  }, [gido]);
  const gidoId      = gido?.gido_id || '';
  const territory   = gido?.territory_id || gido?.jurisdiction || 'IE';

  // ── SmartView solution tiles ──────────────────────────────────────────────
  // State per solution is derived in solutions.js (the single contract), from
  // the signals that are actually maintained (smartdata_status / quota /
  // grants + the live D-001 fileCount) — never the stale licensed_products map.
  const tileCtx = { gido, quota, fileCount };

  const handleAppClick = (solution, state) => {
    // Only solutions with a built detail view are clickable (SmartData today).
    if (isClickable(solution, state)) {
      setCurrentApp(solution.id);
    }
    // Every other state is inert — no navigation, no external links.
  };

  // Show SmartData view
  if (currentApp === 'smartdata') {
    return (
      <SmartData
        gido={gido}
        quota={quota}
        onBack={() => setCurrentApp(null)}
        onRefreshQuota={onRefreshQuota}
      />
    );
  }

  // Show SmartFinance view
  if (currentApp === 'smartfinance') {
    return (
      <SmartFinance
        gido={gido}
        onBack={() => setCurrentApp(null)}
      />
    );
  }

  // Show SmartShop - sub-tile landing (Product CMS / Asset Library / Review)
  if (currentApp === 'smartshop') {
    return (
      <SmartShopHome
        gido={gido}
        onBack={() => setCurrentApp(null)}
      />
    );
  }

  // Show SmartCom (contacts / CRM) view
  if (currentApp === 'smartcom') {
    return (
      <SmartComContacts
        gido={gido}
        onBack={() => setCurrentApp(null)}
      />
    );
  }

  // Main dashboard
  return (
    <>
      {showWelcome && (
        <div className="welcome-overlay">
          <div className="welcome-modal">
            <div className="welcome-modal-logo">
              <img src="/android-chrome-192x192.png" alt="DBOS" className="logo-d-img" />
              <span className="logo-bos">DBOS</span>
            </div>
            <h1 className="welcome-modal-title">
              Welcome to DBOS! 🎉
            </h1>
            <p className="welcome-modal-subtitle">
              Your SmartClient account is ready.
            </p>
            <div className="welcome-gido">
              {gido?.gido_id}
            </div>
            <p className="welcome-modal-hint">
              This is your Global Identity (GIDO).<br/>
              Keep it safe — it's your key to the DBOS ecosystem.
            </p>
            <div className="welcome-modal-actions">
              <button
                className="welcome-bookmark-btn"
                onClick={() => alert('Press Ctrl+D (Windows) or Cmd+D (Mac) to bookmark portal.dbos.world!')}
              >
                ⭐ Bookmark This Page
              </button>
              <button
                className="welcome-enter-btn"
                onClick={() => setShowWelcome(false)}
              >
                Enter My Portal →
              </button>
            </div>
            <p className="welcome-modal-footer">
              portal.dbos.world · V1.0.0
            </p>
          </div>
        </div>
      )}
      <div className="portal">
      {glow && (
        <div
          className={`brand-glow-frame glow-${glow.mode}`}
          style={{
            '--glow-primary': (glow.mode === 'single' && glow.single_color ? glow.colors[glow.single_color] : glow.colors.primary) || '#C9A84C',
            '--glow-secondary': glow.colors.secondary || glow.colors.primary || '#C9A84C',
            '--glow-accent': glow.colors.accent || glow.colors.primary || '#C9A84C',
          }}
        />
      )}

      {/* Header */}
      <header className="portal-header">
        <div className="header-left">
          <div className="portal-logo">
            <img src="/android-chrome-192x192.png" alt="DBOS" className="logo-d-img" />
            <span className="logo-bos">DBOS</span>
          </div>
          <span className="header-title">SmartClient Portal</span>
        </div>
        <div className="header-right">
          <span className="header-signed-in" title={user?.email}>{user?.email}</span>
          <OrgSwitcher orgs={orgs} activeGido={activeGido || gidoId} onSelect={onSelectOrg} />
          <button className="settings-wheel" title="Settings" onClick={() => setShowSettings(true)}>⚙️</button>
          <span className="territory-badge">{territory}</span>
          <button onClick={onLogout} className="logout-button">Sign Out</button>
        </div>
      </header>

      {/* Welcome */}
      <div className="welcome-section">
        <h1>
          {gido?.is_owner === false
              ? <>Welcome to <span className="gold">{companyName}</span></>
              : <>Welcome, <span className="gold">{companyName}</span></>}
          {logoUrl && <img src={logoUrl} alt="Logo" className="welcome-logo" />}
        </h1>
        <p className="welcome-subtitle">
          {gido?.is_owner === false
            ? `You have ${gido.access_source === 'consultant' ? 'consultant' : 'seat'} access to this organisation`
            : 'Your SmartClient Dashboard'} · V1.0.0
        </p>
      </div>

      {/* App launcher — SmartView solution tiles */}
      <div className="section">
        <h2 className="section-title">Your Solutions</h2>
        <div className="apps-grid">
          {SOLUTIONS.map((solution) => {
            const state = deriveTileState(solution, tileCtx);
            return (
              <AppCard
                key={solution.id}
                solution={solution}
                state={state}
                gido={gido}
                onClick={() => handleAppClick(solution, state)}
              />
            );
          })}
        </div>
      </div>

      {/* Trial bar - below apps */}
      {quota?.trial && <TrialBar quota={quota} gido={gido} openUpgrade={openUpgrade} onRefreshQuota={onRefreshQuota} />}
      {!quota?.trial && <PlanBar quota={quota} gido={gido} openUpgrade={openUpgrade} onRefreshQuota={onRefreshQuota} />}
      <UsageBars quota={quota} storageUsedGb={storageUsedGb} fileCount={fileCount} />
      <ReferralCard gido={gido} quota={quota} sub={sub} />

      {/* Bookmark hint - collapses to a small settings button after first interaction */}
      {!bookmarkDismissed && (
        <div className="bookmark-hint">
          <span>💡 Bookmark this page for quick access:</span>
          <strong> portal.dbos.world</strong>
          <button
            className="bookmark-button"
            onClick={() => {
              alert('Press Ctrl+D (Windows) or Cmd+D (Mac) to bookmark this page!');
              setBookmarkDismissed(true);
              localStorage.setItem('dbos_bookmark_dismissed', 'true');
            }}
          >
            ⭐ Save to Bookmarks
          </button>
        </div>
      )}

      {showSettings && <SettingsPanel gido={gido} onClose={() => setShowSettings(false)} onPreviewGlow={setGlow} onRefresh={onRefresh} />}

      {/* Updates feed */}
      <UpdatesFeed />

      {/* Roadmap */}
      <Roadmap />

      {/* Footer */}
      <footer className="portal-footer">
        <span>DBOS Digital Business Operating System</span>
        <span>V1.0.0 · {territory} Territory</span>
      </footer>

    </div>
    </>
  );
}

export default Dashboard;